/**
 * The coach's one line over the hand — "Coach: Play Q♠" — and, a tap away, the
 * sheet that says why and how every other card fared in the simulations.
 *
 * The line is short on purpose: the hint row is 24 px and one line, and the
 * card itself is already ringed in the fan. The reasoning is there for anyone
 * who wants it, not in the way of anyone who does not.
 */
import { useState } from 'react'
import { SuitText } from '../table/atoms'
import { Sheet } from '../table/PromptSheet'
import type { TableView } from '../table/view'
import { chatgptUrl, coachPrompt } from './chatgpt'
import { adviceHeadline, type CoachState } from './useCoach'

export function CoachLine({ view, state }: { view: TableView; state: CoachState }): JSX.Element {
  const [open, setOpen] = useState(false)
  if (state.status === 'thinking') {
    return (
      <div className="hint-text coach-line thinking" role="status">
        <b>Coach</b> is thinking…
      </div>
    )
  }
  if (state.status === 'error') {
    return <div className="hint-text coach-line muted">Coach is unavailable here</div>
  }
  const advice = state.advice
  return (
    <>
      <button type="button" className="hint-text coach-line" onClick={() => setOpen(true)} aria-label="Why the coach suggests this">
        <b>Coach</b>
        <span className="coach-pick-text">
          <SuitText text={adviceHeadline(view, advice.action)} />
        </span>
        <span className="coach-why">why?</span>
      </button>
      {open ? <CoachSheet view={view} state={state} onClose={() => setOpen(false)} /> : null}
    </>
  )
}

function CoachSheet({
  view,
  state,
  onClose,
}: {
  view: TableView
  state: Extract<CoachState, { status: 'ready' }>
  onClose: () => void
}): JSX.Element {
  const advice = state.advice
  const ranked = advice.ranked
  const top = ranked.length > 0 ? Math.max(...ranked.map((c) => c.mean)) : 0
  const bottom = ranked.length > 0 ? Math.min(...ranked.map((c) => c.mean)) : 0
  const span = top - bottom
  const pick = advice.pickKey
  const calls = ranked.some((c) => c.card === undefined)
  return (
    <Sheet onDismiss={onClose}>
      <div className="sheet-body coach-sheet">
        <h2>
          <SuitText text={adviceHeadline(view, advice.action)} />
        </h2>
        <p>
          <SuitText text={advice.reason} />
        </p>
        {ranked.length > 1 ? (
          <>
            <div className="coach-table" role="table" aria-label="How each card fared">
              <div className="coach-row head" role="row">
                <span role="columnheader">{calls ? 'Call' : 'Card'}</span>
                <span role="columnheader">Avg. result, pts</span>
                <span role="columnheader">Best in</span>
              </div>
              {ranked.map((c) => (
                <div className={c.key === pick ? 'coach-row pick' : 'coach-row'} role="row" key={c.key}>
                  <span role="cell" className="coach-card">
                    <SuitText text={`${c.label.charAt(0).toUpperCase()}${c.label.slice(1)}${c.nominate ? ` naming ${GLYPH[c.nominate]}` : ''}`} />
                  </span>
                  <span role="cell" className="coach-bar">
                    <i style={{ width: `calc((100% - 44px) * ${(span < 0.5 ? 1 : 0.08 + 0.92 * ((c.mean - bottom) / span)).toFixed(3)})` }} />
                    <em>{signedPts(c.mean)}</em>
                  </span>
                  <span role="cell" className="coach-share">{Math.round(c.bestShare * 100)}%</span>
                </div>
              ))}
            </div>
            <p className="setting-note">
              {advice.worlds} deals simulated from what you can see — the hidden hands{calls ? ' and the kitty' : ''} dealt at
              random, respecting every void shown and the bidding — and each {calls ? 'call' : 'card'} played out to the end of the hand.
            </p>
          </>
        ) : (
          <p className="setting-note">The house bot&apos;s choice from this seat, from only what you can see.</p>
        )}
        <AskChatGpt view={view} state={state} />
        <div className="option-row">
          <button className="btn btn-primary" onClick={onClose}>
            Got it
          </button>
        </div>
      </div>
    </Sheet>
  )
}

/**
 * Take the position to ChatGPT: a link that opens a new chat with the whole
 * hand written out and the coach's numbers attached. The prompt is copied as
 * well, for the phone app, which opens the link but does not fill the
 * composer from it.
 */
function AskChatGpt({ view, state }: { view: TableView; state: Extract<CoachState, { status: 'ready' }> }): JSX.Element | null {
  const [copied, setCopied] = useState(false)
  const prompt = coachPrompt(view, state.advice)
  if (!prompt) return null
  const copy = () => {
    navigator.clipboard?.writeText(prompt).then(
      () => setCopied(true),
      () => {},
    )
  }
  return (
    <div className="coach-ask">
      <a className="btn btn-secondary" href={chatgptUrl(prompt)} target="_blank" rel="noopener noreferrer" onClick={copy}>
        Ask ChatGPT to coach me
      </a>
      {copied ? <p className="setting-note">Prompt copied too, in case ChatGPT opens without it.</p> : null}
    </div>
  )
}

const GLYPH = { S: '♠', H: '♥', D: '♦', C: '♣' } as const

function signedPts(n: number): string {
  const v = Math.abs(n) >= 10 ? Math.round(n) : Math.round(n * 10) / 10
  return `${v > 0 ? '+' : ''}${v}`
}
